/**
 * @file SimpleDrawer.js
 * @description Represents a simple drawer component that allows users to select colors
 */

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import style from './ThemePage.module.scss';
import { getText } from '../../i18n';

const drawerBaseStyles = css`
    position: fixed;
    right: 0;
    width: 350px;
    background: #fff;
    z-index: 1002;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    overflow-y: auto;
`;

const overlayBaseStyles = css`
    position: fixed;
    left: 0;
    width: 100vw;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
`;

const SimpleDrawer = ({
    isOpen,
    closeDrawer,
    onColorChange,
    initialColors,
    drawerType,
    allowGradient = true,
    startBelowHeader = true,
}) => {
    const [colorType, setColorType] = useState('single');
    const [gradientColor, setGradientColor] = useState({
        left: '#ffffff',
        right: '#ffffff',
    });
    const [activePicker, setActivePicker] = useState('left');
    const [initialColor, setInitialColor] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setColorType(drawerType);
            const updatedColors = {
                left: initialColors.left,
                right:
                    drawerType === 'gradient'
                        ? initialColors.right
                        : initialColors.left,
            };
            setGradientColor(updatedColors);
            setInitialColor(updatedColors);
        }
    }, [isOpen, initialColors, drawerType]);

    const updateColorType = type => {
        setColorType(type);
    };

    const handleChangeComplete = color => {
        const newColor = color.hex;
        const updatedColors =
            colorType === 'single'
                ? { left: newColor, right: newColor }
                : { ...gradientColor, [activePicker]: newColor };

        setGradientColor(updatedColors);
    };

    const handleSave = () => {
        if (onColorChange) {
            onColorChange(colorType, gradientColor);
        }
        closeDrawer();
    };

    const handleClose = () => {
        setGradientColor(initialColor);
        closeDrawer();
    };

    const gradientPreviewStyle = css`
        height: 20px;
        width: 100%;
        background: linear-gradient(
            to right,
            ${gradientColor.left},
            ${gradientColor.right}
        );
        margin: 20px 0;
        border: 1px solid var(--alpha-black-20, rgba(47, 43, 67, 0.2));
        box-shadow:
            0px -1px 0px 0px rgba(47, 43, 67, 0.1) inset,
            0px 1px 3px 0px rgba(47, 43, 67, 0.1);
    `;

    const leftSelectorStyle = css`
        background: ${gradientColor.left};
    `;
    const rightSelectorStyle = css`
        background: ${gradientColor.right};
    `;

    const deployedEnvName = process.env.REACT_APP_DEPLOYED_ENV_NAME || 'Local';

    const baseHeaderHeight = 80; // px, height of the header
    const additionalOffset = !deployedEnvName.toUpperCase().includes('PROD')
        ? 48 // extra offset for specific env banner
        : 0;

    const totalTopOffset = startBelowHeader
        ? baseHeaderHeight + additionalOffset
        : 0;

    if (!isOpen) return null;

    const drawerStyles = css`
        ${drawerBaseStyles};
        top: ${totalTopOffset}px;
        height: calc(100vh - ${totalTopOffset}px);
    `;

    const overlayStyles = css`
        ${overlayBaseStyles};
        top: ${totalTopOffset}px;
        height: calc(100vh - ${totalTopOffset}px);
    `;

    return ReactDOM.createPortal(
        <>
            <div css={overlayStyles} onClick={handleClose} />
            <div className={style.colorContainer} css={drawerStyles}>
                <div className="align-items-center d-flex float-end justify-content-end mb-3 w-100">
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={handleClose}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ')
                                handleClose();
                        }}
                        className="ing-text-buttons mx-3"
                    >
                        <i className="bi bi-x me-2" />
                        {getText('msg_77dfd2135f4d')}
                    </span>

                    <span
                        type="button"
                        onClick={handleSave}
                        className="ing-text-buttons gap-1 d-inline-flex align-items-center ms-3"
                    >
                        <i className="bi bi-check-lg me-2" />
                        {getText('msg_cfea419c3b4e')}
                    </span>
                </div>

                <div className={style.colorTypeContainer}>
                    <div>
                        <input
                            type="radio"
                            id="single"
                            name="colorType"
                            value="single"
                            checked={colorType === 'single'}
                            onChange={() => updateColorType('single')}
                            className={style.singleTypeSelector}
                        />
                    </div>
                    {allowGradient && (
                        <div>
                            <input
                                type="radio"
                                id="gradient"
                                name="colorType"
                                value="gradient"
                                checked={colorType === 'gradient'}
                                onChange={() => updateColorType('gradient')}
                                className={style.gradientTypeSelector}
                            />
                        </div>
                    )}
                </div>

                <div className={`my-4 mt-5 ${style.colorBarPreview}`}>
                    <input
                        type="radio"
                        id="left"
                        name="gradientDirection"
                        checked={
                            activePicker === 'left' && colorType !== 'single'
                        }
                        onChange={() => setActivePicker('left')}
                        disabled={colorType === 'single'}
                        className={style.leftSelector}
                        css={leftSelectorStyle}
                    />

                    <input
                        type="radio"
                        id="right"
                        name="gradientDirection"
                        checked={
                            activePicker === 'right' && colorType !== 'single'
                        }
                        onChange={() => setActivePicker('right')}
                        disabled={colorType === 'single'}
                        className={style.rightSelector}
                        css={rightSelectorStyle}
                    />
                    <div
                        className={style.previewBar}
                        css={gradientPreviewStyle}
                    />
                </div>

                <ChromePicker
                    width="100%"
                    color={gradientColor[activePicker]}
                    onChangeComplete={handleChangeComplete}
                    disableAlpha={true}
                />
            </div>
        </>,
        document.body,
    );
};

SimpleDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    onColorChange: PropTypes.func.isRequired,
    initialColors: PropTypes.shape({
        left: PropTypes.string,
        right: PropTypes.string,
    }),
    drawerType: PropTypes.oneOf(['single', 'gradient']),
    allowGradient: PropTypes.bool,
    startBelowHeader: PropTypes.bool,
};

export default SimpleDrawer;
